import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useGetList, type Identifier } from "ra-core";
import { useCallback, useEffect, useId } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { unreadCount, type TeamMessage } from "./teamChat";

/** Reloads the list whenever someone writes, edits or deletes a message. */
const useLiveReload = (reload: () => void) => {
  const channelName = `team-chat-${useId()}`;
  useEffect(() => {
    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "gula_team_messages" },
        () => reload(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelName, reload]);
};

/**
 * Latest team messages, newest first, kept live. Only what people write
 * (messages and tasks): the CRM's own news (sales, deliveries, hot leads...)
 * live in Avisos and Movimientos de envíos on Inicio, not in the chat.
 */
export const useTeamFeed = (perPage = 300) => {
  const {
    data = [],
    refetch,
    isPending,
  } = useGetList<TeamMessage>(
    "gula_team_feed",
    {
      filter: { "kind@neq": "event" },
      pagination: { page: 1, perPage },
      sort: { field: "created_at", order: "DESC" },
    },
    { refetchInterval: 60_000 },
  );
  const reload = useCallback(() => {
    refetch();
  }, [refetch]);
  useLiveReload(reload);
  return { messages: data, refetch: reload, isPending };
};

const readKey = (meId?: Identifier) => ["gula-team-read", String(meId)];

/** When the signed-in user last looked at the chat. */
export const useTeamRead = () => {
  const { identity } = useGetIdentity();
  const meId = identity?.id;
  const queryClient = useQueryClient();
  const { data: lastReadAt } = useQuery({
    queryKey: readKey(meId),
    enabled: meId != null,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("gula_team_reads")
        .select("last_read_at")
        .eq("sales_id", meId)
        .maybeSingle();
      return (data?.last_read_at as string | undefined) ?? null;
    },
  });
  const markRead = useCallback(async () => {
    if (meId == null) return;
    const now = new Date().toISOString();
    await getSupabaseClient()
      .from("gula_team_reads")
      .upsert({ sales_id: meId, last_read_at: now });
    queryClient.setQueryData(readKey(meId), now);
  }, [meId, queryClient]);
  return { meId, lastReadAt, markRead };
};

/** Unread messages for the header badge. */
export const useTeamUnread = () => {
  const { messages } = useTeamFeed(100);
  const { meId, lastReadAt } = useTeamRead();
  if (lastReadAt === undefined) return 0;
  return unreadCount(messages, lastReadAt, meId);
};
